import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { Alert, Box, Button, Chip, Divider, Paper, Stack, Typography } from "@mui/material";
import { CustomGridColDef, DataGrid } from "components/DataGrid";
import { useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import type { BenchmarkDefinition, CriterionDefinition, CriterionDetails } from "./types";

import { CriterionDetailsDialog, QuestionSourceLinks } from "./ScoutEvalCommon";
import {
  buildBenchmarksUrl,
  compactPanelSx,
  formatEnumLabel,
  formatPoints,
  formatQuestionName,
  getAggregationRoleChipColor,
  panelSx,
  shouldShowAggregationRole,
  titleFromKey,
} from "./scoutEvalUtils";

function getCriterionDetailsFromDefinition(criterion: CriterionDefinition): CriterionDetails {
  return {
    criterionKey: criterion.criterionKey,
    category: criterion.category,
    scorerType: criterion.scorerType,
    scoreAggregationRole: criterion.scoreAggregationRole,
    maxPoints: criterion.maxPoints,
    instructions: criterion.instructions,
    requirements: criterion.requirements,
    enabled: criterion.enabled,
  };
}

function BenchmarksList({ benchmarks }: { benchmarks: BenchmarkDefinition[] }) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const selectedBenchmarkKey = searchParams.get("benchmark") ?? benchmarks[0]?.key ?? "";
  const selectedBenchmark =
    benchmarks.find((benchmark) => benchmark.key === selectedBenchmarkKey) ?? benchmarks[0] ?? null;

  const benchmarkColumns = useMemo<CustomGridColDef<BenchmarkDefinition>[]>(
    () => [
      {
        field: "name",
        headerName: "Benchmark",
        flex: 1,
        minWidth: 260,
        renderCell(params) {
          return (
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="body2">{params.row.name}</Typography>
              <Chip label={`v${params.row.version}`} color="info" size="small" variant="outlined" />
              {!params.row.enabled ? (
                <Chip label="Disabled" size="small" variant="outlined" />
              ) : null}
            </Stack>
          );
        },
      },
      {
        field: "questions",
        headerName: "Questions",
        width: 130,
        valueGetter(_, row) {
          return row.questions.length;
        },
      },
    ],
    []
  );

  if (benchmarks.length === 0) {
    return (
      <Alert severity="info" variant="outlined">
        No Scout eval benchmarks have been seeded yet.
      </Alert>
    );
  }

  return (
    <Stack spacing={2}>
      <Paper elevation={0} sx={panelSx}>
        <Box sx={{ height: 250, width: "100%" }}>
          <DataGrid
            rows={benchmarks}
            columns={benchmarkColumns}
            getRowId={(row) => row.id}
            onRowClick={({ row }) => {
              navigate(buildBenchmarksUrl({ view: "benchmarks", benchmarkKey: row.key }));
            }}
            getRowLink={(row) => buildBenchmarksUrl({ view: "benchmarks", benchmarkKey: row.key })}
            noRowsMessage="No benchmark definitions found."
          />
        </Box>
      </Paper>
      {selectedBenchmark ? <BenchmarkDetail benchmark={selectedBenchmark} /> : null}
    </Stack>
  );
}

function CriterionDetail({ criterion }: { criterion: CriterionDefinition }) {
  const [detailsOpen, setDetailsOpen] = useState(false);
  const criterionDetails = getCriterionDetailsFromDefinition(criterion);

  return (
    <>
      <Paper variant="outlined" sx={compactPanelSx}>
        <Stack spacing={0.75}>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            <Typography variant="body2" fontWeight={700}>
              {titleFromKey(criterion.criterionKey)}
            </Typography>
            <Chip
              label={`${formatPoints(criterion.maxPoints)} pts`}
              color="info"
              size="small"
              variant="outlined"
            />
            {shouldShowAggregationRole(criterion.scoreAggregationRole) ? (
              <Chip
                label={formatEnumLabel(criterion.scoreAggregationRole)}
                color={getAggregationRoleChipColor(criterion.scoreAggregationRole)}
                size="small"
                variant="outlined"
              />
            ) : null}
            <Chip
              label={formatEnumLabel(criterion.scorerType)}
              color="default"
              size="small"
              variant="outlined"
            />
            {!criterion.enabled ? <Chip label="Disabled" size="small" variant="outlined" /> : null}
          </Stack>
          <Typography variant="caption" color="text.secondary">
            {formatEnumLabel(criterion.category)}
          </Typography>
          <Button
            size="small"
            variant="text"
            startIcon={<InfoOutlinedIcon />}
            onClick={() => setDetailsOpen(true)}
            sx={{ alignSelf: "flex-start" }}
          >
            View details
          </Button>
        </Stack>
      </Paper>
      <CriterionDetailsDialog
        open={detailsOpen}
        onClose={() => setDetailsOpen(false)}
        criterion={criterionDetails}
      />
    </>
  );
}

function BenchmarkDetail({ benchmark }: { benchmark: BenchmarkDefinition }) {
  return (
    <Stack spacing={2}>
      <Paper elevation={0} sx={panelSx}>
        <Stack spacing={1}>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            <Typography variant="h6">{benchmark.name}</Typography>
            <Chip label={`v${benchmark.version}`} color="info" size="small" variant="outlined" />
            {!benchmark.enabled ? <Chip label="Disabled" size="small" variant="outlined" /> : null}
          </Stack>
          {benchmark.description ? (
            <Typography variant="body2" color="text.secondary">
              {benchmark.description}
            </Typography>
          ) : null}
        </Stack>
      </Paper>
      <Stack spacing={2}>
        {benchmark.questions.map((question, questionIndex) => (
          <Paper key={question.id} variant="outlined" sx={panelSx}>
            <Stack spacing={1.5}>
              <Stack
                direction={{ xs: "column", md: "row" }}
                justifyContent="space-between"
                spacing={1}
              >
                <Stack spacing={0.25}>
                  <Typography variant="subtitle1" fontWeight={700}>
                    {questionIndex + 1}. {formatQuestionName(question.questionKey)}
                  </Typography>
                </Stack>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  <Chip
                    label={`Weight ${formatPoints(question.weight)}`}
                    color="info"
                    size="small"
                    variant="outlined"
                  />
                  {!question.enabled ? (
                    <Chip label="Disabled" size="small" variant="outlined" />
                  ) : null}
                </Stack>
              </Stack>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Question
                </Typography>
                <Typography variant="body2">{question.prompt}</Typography>
              </Box>
              <QuestionSourceLinks sourceLinks={question.sourceLinks} />
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Expected answer
                </Typography>
                <Typography variant="body2">{question.expectedAnswer}</Typography>
              </Box>
              <Divider />
              <Stack spacing={1}>
                <Typography variant="subtitle2">Criteria definitions</Typography>
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: {
                      xs: "1fr",
                      md: "repeat(2, minmax(0, 1fr))",
                    },
                    gap: 1.5,
                  }}
                >
                  {question.criteria.map((criterion) => (
                    <CriterionDetail key={criterion.id} criterion={criterion} />
                  ))}
                </Box>
              </Stack>
            </Stack>
          </Paper>
        ))}
      </Stack>
    </Stack>
  );
}

export function BenchmarksTab({ benchmarks }: { benchmarks: BenchmarkDefinition[] }) {
  return <BenchmarksList benchmarks={benchmarks} />;
}
